import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { getLocale } from '@/services/utils'
import clsx from 'clsx'
import { formatDistance } from 'date-fns'
import capitalize from 'lodash/capitalize'

import { NotificationsAPI } from '@makyoapp/makyo-sdk'
import { NotificationItemAdapter, NotificationItemField } from '@makyoapp/makyo-common'
import { CaretDown, Setting24 } from '@makyoapp/makyo-icon/icon'
import {
  Checkbox,
  DD,
  DDItem,
  Loading,
  Table,
  Td,
  Th,
  Tr,
  TrNotFound,
  Twemoji,
  WrappedPagination,
} from '@makyoapp/makyo-ui'

import { useMyNotificationsContext } from '@/context/MyNotificationsContext'
import { useNotificationsContext } from '@/context/NotificationsContext'
import useAuth from '@/hooks/useAuth'
import {
  NOTIFICATION_FIELDS,
  NOTIFICATIONS_ROWS_PER_PAGE,
  USER_NOTIFICATION_SETTING_ROUTE,
  USER_ROUTE,
} from '@/shared/constants'
import { WrapperProps } from '@/shared/routes'
import { NotifModal } from '@/components/AppModals/NotifModal'
import Layout from '@/components/Layout'

import useNotificationsLayout from './hooks/useNotificationsLayout'
import useNotificationsTable from './hooks/useNotificationsTable'

interface NotificationItemProps {
  /**
   * which field to show
   */
  field: NotificationItemField
  /**
   * the notification
   */
  item: NotificationItemAdapter
  /**
   * on click event
   */
  onClick: () => void
}

/**
 * notification item cell
 */
function NotificationItem({ field, item, onClick }: NotificationItemProps) {
  const { userData } = useAuth()

  switch (field) {
    case 'title':
      return (
        <Td id={`${item.id}-title`} onClick={onClick} className='notif-icon-td name-long'>
          <div className='flex items-center gap-3 txt'>
            <Twemoji className='p-0 w-5 h-5 notif-icon' emoji={item.icon} />
            <div className='txt notif-title'>{item.title}</div>
          </div>
        </Td>
      )
    case 'content':
      return (
        <Td id={`${item.id}-content`} onClick={onClick} className='notif-content-id long-td'>
          <div className='txt notif-content'>{item.content?.replaceAll(/<[^>]*>?/gm, ' ').trim()}</div>
        </Td>
      )
    case 'created': {
      const lang = userData?.lang
      const now = new Date()
      return (
        <Td id={`${item.id}-created`} onClick={onClick} align='right' className='date notif-created-td'>
          {formatDistance(item.created, now, {
            addSuffix: true,
            locale: getLocale(lang),
          })}
        </Td>
      )
    }
    default:
      // For other fields, simply convert the field to string
      return <Td onClick={onClick}>{item[field]?.toString()}</Td>
  }
}

interface NotificationRowProps {
  /**
   * the notification item
   */
  notification: NotificationItemAdapter
  /**
   * update read state
   */
  setIsRead: (notification: NotificationItemAdapter) => void
}

/**
 * handle render of table row
 */
function NotificationRow({ notification, setIsRead }: NotificationRowProps) {
  const { checks, setChecks, setSelectedNotif, page } = useMyNotificationsContext()
  const isRead = notification.isRead
  const isCurrentlyChecked = checks.find((c) => c.id === notification.id)
  const { updateRead, unreadCount, setUnreadCount } = useNotificationsContext()

  const handleClick = () => {
    if (!isRead) {
      NotificationsAPI.ReqRead({
        _Notifs: [notification.id],
      })
        .then(() => {
          setIsRead(notification)
          setUnreadCount(unreadCount - 1)
          if (page === 1) {
            updateRead([notification.id])
          }
        })
        .catch((e) => {})
    }
    setSelectedNotif(notification)
  }

  return (
    <Tr
      key={`tr-${notification.id}`}
      data-trigger-id={notification.trigger}
      className={clsx(!isRead ? 'font-semibold' : 'text-light')}
    >
      <Td className='chk'>
        <Checkbox
          inputClassName='mk-chk'
          id={`${notification.id}-chk`}
          checked={isCurrentlyChecked !== undefined}
          onClick={() => {
            if (isCurrentlyChecked) {
              setChecks(checks.filter((c) => c.id !== notification.id))
            } else {
              setChecks([...checks, notification])
            }
          }}
        />
      </Td>
      {NOTIFICATION_FIELDS.map((f) => {
        return <NotificationItem key={`${f}-${notification.id}`} item={notification} field={f} onClick={handleClick} />
      })}
    </Tr>
  )
}

/**
 * Notification Table
 */
function NotificationTable() {
  // Use translation hook for multi-language support
  const { t } = useTranslation()

  // Use custom hook to manage notifications table state and interactions
  const { checked, notifications, isLoading, selectedNotif, setSelectedNotif, handleCheck, handleRead } =
    useNotificationsTable()

  return (
    <div className='relative'>
      {isLoading && (
        <div className='absolute inset-0 bg-white opacity-50 flex items-center justify-center'>
          <Loading fullHeight={false} />
        </div>
      )}
      <Table auto={false} className='border bdr tbl-hov'>
        <thead>
          <Tr>
            <Th className='chk'>
              <Checkbox checked={checked} onClick={handleCheck} />
            </Th>
            {NOTIFICATION_FIELDS.map((f) => {
              return (
                <Th key={`th-${f}`} align={f === 'created' ? 'right' : undefined}>
                  {t(capitalize(f))}
                </Th>
              )
            })}
          </Tr>
        </thead>
        <tbody>
          {notifications.items.length == 0 && !isLoading ? (
            <TrNotFound colSpan={NOTIFICATION_FIELDS.length + 1} />
          ) : (
            notifications.items.map((notification) => {
              return (
                <NotificationRow
                  key={notification.id}
                  setIsRead={() => handleRead(notification)}
                  notification={notification}
                />
              )
            })
          )}
        </tbody>
      </Table>

      {selectedNotif && (
        <NotifModal
          show
          notification={selectedNotif}
          onHide={() => {
            setSelectedNotif(undefined)
          }}
        />
      )}
    </div>
  )
}

/**
 * My Notifications page layout
 *
 * depends on `MyNotificationsContext`
 */
function NotificationsLayout(props: WrapperProps) {
  const { isActionDisabled, handleMark, handleUnread, t, notifications, page, onPageChange, handleDeleteSelected } =
    useNotificationsLayout()

  return (
    <Layout
      title={props.title}
      actions={
        <>
          <Link
            className='w-9 h-9 flex-cc hover:bg-primary-500 hover:text-white transition-colors rounded-sm'
            to={`${USER_ROUTE}/${USER_NOTIFICATION_SETTING_ROUTE}`}
          >
            <Setting24 width={16} height={16} />
          </Link>
          <DD
            toggleNoButton={true}
            placement='bottom-end'
            menuClassName='dd-menu-alt w-[calc(100%+10px)]'
            toggle={(active, toggleProps) => {
              return (
                <div
                  aria-disabled={isActionDisabled}
                  className={clsx(
                    'rounded-sm font-semibold text-white flex items-center h-9',
                    isActionDisabled
                      ? 'bg-zinc-300 text-zinc-500 hover:bg-zinc-300 active:bg-zinc-300'
                      : 'bg-primary-500 hover:bg-primary-600'
                  )}
                >
                  <button
                    id='mark-selected-as-read'
                    disabled={isActionDisabled}
                    className='px-4 h-full'
                    onClick={handleMark}
                  >
                    {t('Mark selected as read')}
                  </button>
                  <span className='w-[1px] h-full bg-white dark:bg-slate-700' />
                  <button {...toggleProps} className='flex items-center px-2 h-full'>
                    <CaretDown />
                  </button>
                </div>
              )
            }}
          >
            <DDItem
              id='mark-selected-as-unread'
              disabled={isActionDisabled}
              className='dd-menu-row'
              onClick={handleUnread}
            >
              <div className={clsx('dd-menu-item-inner', isActionDisabled && 'text-light')}>
                {t('Mark selected as unread')}
              </div>
            </DDItem>
            <DDItem
              id='delete-selected'
              disabled={isActionDisabled}
              className='dd-menu-row'
              onClick={handleDeleteSelected}
            >
              <div className={clsx('dd-menu-item-inner', isActionDisabled && 'text-light')}>{t('Delete selected')}</div>
            </DDItem>
          </DD>
        </>
      }
    >
      <NotificationTable />
      <WrappedPagination
        id='myn-pg'
        totalCount={notifications.totalCount}
        page={page}
        perPage={NOTIFICATIONS_ROWS_PER_PAGE}
        onChange={onPageChange}
      />
    </Layout>
  )
}

export default NotificationsLayout
